import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ulid } from 'ulid';
import { useAuth } from '../auth';
import { api, getAccessToken } from '../lib/api';
import { uploadDocument } from '../lib/upload';
import { getSocket } from '../lib/socket';
import type { CreditPhoto, CreditRequest, MessagesPage, RequestMessage } from '../types';

export function RequestDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isStaff = !!(me?.roles.includes('admin') || me?.roles.includes('customer_service'));
  const isAdmin = !!me?.roles.includes('admin');

  const reqQ = useQuery({
    queryKey: ['credit-request', id],
    queryFn: () => api<CreditRequest>(`/api/v1/credit-requests/${id}`),
    enabled: !!id,
  });

  const msgsQ = useQuery({
    queryKey: ['messages', id],
    queryFn: () => api<MessagesPage>(`/api/v1/credit-requests/${id}/messages?limit=200`),
    enabled: !!id,
  });

  useEffect(() => {
    if (!id) return;
    const s = getSocket();
    s.emit('request:join', { requestId: id });
    const onNew = (m: RequestMessage) => {
      if (m.requestId !== id) return;
      qc.setQueryData<MessagesPage>(['messages', id], (prev) => {
        if (!prev) return { items: [m], nextCursor: null };
        if (prev.items.some((x) => x.id === m.id || x.clientId === m.clientId)) return prev;
        return { ...prev, items: [...prev.items, m] };
      });
    };
    const onStatus = (p: { id: string; status: 'delivered' | 'read' }) => {
      qc.setQueryData<MessagesPage>(['messages', id], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((m) => (m.id === p.id ? { ...m, status: p.status, readAt: p.status === 'read' ? new Date().toISOString() : m.readAt } : m)),
        };
      });
    };
    const onChanged = () => qc.invalidateQueries({ queryKey: ['credit-request', id] });

    s.on('message:new', onNew);
    s.on('message:status', onStatus);
    s.on('request:status_changed', onChanged);
    s.on('request:assigned', onChanged);
    s.on('request:closed', onChanged);
    s.on('request:reopened', onChanged);

    return () => {
      s.off('message:new', onNew);
      s.off('message:status', onStatus);
      s.off('request:status_changed', onChanged);
      s.off('request:assigned', onChanged);
      s.off('request:closed', onChanged);
      s.off('request:reopened', onChanged);
    };
  }, [id, qc]);

  const [actionError, setActionError] = useState<string | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmTakeover, setConfirmTakeover] = useState(false);
  const [reportBoxesOpen, setReportBoxesOpen] = useState(false);

  const assignMut = useMutation({
    mutationFn: () => api<CreditRequest>(`/api/v1/credit-requests/${id}/assign`, { method: 'POST' }),
    onMutate: () => setActionError(null),
    onSuccess: (data) => qc.setQueryData(['credit-request', id], data),
    onError: (err) => setActionError(err instanceof Error ? err.message : 'Error al asignar'),
  });

  const reopenMut = useMutation({
    mutationFn: () => api<CreditRequest>(`/api/v1/credit-requests/${id}/reopen`, { method: 'POST' }),
    onMutate: () => setActionError(null),
    onSuccess: (data) => qc.setQueryData(['credit-request', id], data),
    onError: (err) => setActionError(err instanceof Error ? err.message : 'Error al reabrir'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api(`/api/v1/credit-requests/${id}`, { method: 'DELETE' }),
    onMutate: () => setActionError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-requests'] });
      qc.removeQueries({ queryKey: ['credit-request', id] });
      navigate('/');
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : 'Error al eliminar'),
  });

  if (reqQ.isLoading) return <div className="text-slate-400 text-center py-10 text-lg">Cargando…</div>;
  if (reqQ.isError || !reqQ.data) return <div className="text-rose-700 py-8 text-lg">No se pudo cargar.</div>;

  const cr = reqQ.data;
  // Defensive guard: if a mutation cached a partial response without the
  // embedded `store` relation, treat it as still loading and let the
  // background invalidate refetch it. Better than a runtime crash.
  if (!cr.store) return <div className="text-slate-400 text-center py-10 text-lg">Actualizando…</div>;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link to="/" className="text-base text-brand-700 hover:underline font-medium">← Volver</Link>
            <h1 className="text-2xl font-bold text-slate-900 truncate mt-1">{cr.claimNumber}</h1>
            <p className="text-base text-slate-500 truncate mt-1">
              {cr.store.name} · {format(new Date(cr.createdAt), 'dd/MM/yyyy HH:mm')}
            </p>
          </div>
          <StatusBadge status={cr.status} />
        </div>

        {cr.boxesAffected != null && (
          <p className="text-base mt-3 text-slate-700">
            <span className="font-semibold">{cr.boxesAffected}</span> cajas afectadas
            {cr.notes ? ` · ${cr.notes}` : ''}
          </p>
        )}

        {isStaff && cr.creditType === 'open_type' && cr.status !== 'closed' && cr.boxesAffected == null && (
          <div className="mt-4 bg-amber-50 border-2 border-amber-300 rounded-2xl p-4">
            <p className="text-base text-amber-900 font-semibold">
              🛠️ El encargado está trabajando este crédito
            </p>
            <p className="text-sm text-amber-800 mt-1">
              Va a tratar de salvar lo que pueda y reportará el número final de cajas
              afectadas. Espera su reporte antes de cerrar.
            </p>
          </div>
        )}

        {!isStaff && cr.creditType === 'open_type' && cr.status !== 'closed' && (
          <div className="mt-4">
            {cr.boxesAffected == null ? (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4">
                <p className="text-base text-emerald-900 font-semibold">
                  📦 Cuando termines de revisar el producto
                </p>
                <p className="text-sm text-emerald-800 mt-1">
                  Reporta cuántas cajas finalmente quedaron dañadas. CS confirmará el cierre.
                </p>
                <button
                  type="button"
                  onClick={() => setReportBoxesOpen(true)}
                  className="mt-3 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-base font-bold rounded-full shadow-md"
                >
                  Reportar cajas afectadas
                </button>
              </div>
            ) : (
              <div className="bg-sky-50 border-2 border-sky-200 rounded-2xl p-4">
                <p className="text-base text-sky-900">
                  Reportaste{' '}
                  <span className="font-bold">
                    {cr.boxesAffected} caja{cr.boxesAffected === 1 ? '' : 's'}
                  </span>
                  {cr.boxesAffectedReportedAt && (
                    <> el {format(new Date(cr.boxesAffectedReportedAt), 'dd/MM HH:mm')}</>
                  )}
                  . Esperando confirmación de CS.
                </p>
                <button
                  type="button"
                  onClick={() => setReportBoxesOpen(true)}
                  className="mt-3 px-4 py-2.5 bg-white border-2 border-sky-300 hover:border-sky-500 text-sky-800 text-base font-semibold rounded-full"
                >
                  Actualizar
                </button>
              </div>
            )}
          </div>
        )}

        {isStaff && (
          <>
            {cr.agent && (
              <p className="mt-3 text-base text-slate-600">
                <span className="text-slate-400">Atendido por:</span>{' '}
                <span className="font-semibold text-slate-800">{cr.agent.name}</span>
                {cr.agent.userId === me?.id && <span className="text-emerald-700"> · tú</span>}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {cr.status !== 'closed' && cr.agent?.userId !== me?.id && (
                <button
                  onClick={() => (cr.agent ? setConfirmTakeover(true) : assignMut.mutate())}
                  disabled={assignMut.isPending}
                  className="px-5 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-base font-semibold rounded-full"
                >
                  {assignMut.isPending ? '…' : cr.agent ? 'Tomar caso' : 'Asignarme'}
                </button>
              )}
              {cr.status === 'processing' && cr.agent?.id && (
                <button
                  onClick={() => setCloseOpen(true)}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold rounded-full"
                >
                  Cerrar crédito
                </button>
              )}
              {cr.status === 'closed' && (
                <button
                  onClick={() => reopenMut.mutate()}
                  disabled={reopenMut.isPending}
                  className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white text-base font-semibold rounded-full"
                >
                  {reopenMut.isPending ? '…' : 'Reabrir'}
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-base font-semibold rounded-full"
                  title="Eliminar crédito permanentemente"
                >
                  🗑️ Eliminar
                </button>
              )}
            </div>
            {actionError && (
              <p className="mt-3 text-base text-rose-700 bg-rose-50 border-2 border-rose-200 rounded-xl p-3 font-medium">
                {actionError}
              </p>
            )}
          </>
        )}
      </div>

      {Array.isArray(cr.photos) && cr.photos.length > 0 && (
        <PhotoGallery requestId={id} photos={cr.photos} isStaff={isStaff} />
      )}

      {isStaff && cr.status === 'closed' && cr.documentStatus !== 'document_sent' && (
        <DocumentUploadSection requestId={id} />
      )}

      {cr.creditDocumentUrl && (
        <DocumentReadySection
          requestId={id}
          url={cr.creditDocumentUrl}
          amount={cr.creditAmount ?? null}
          claimNumber={cr.claimNumber}
          isStaff={isStaff}
        />
      )}

      <Chat
        requestId={id}
        messages={msgsQ.data?.items ?? []}
        isLoading={msgsQ.isLoading}
      />

      {closeOpen && (
        <CloseDialog
          requestId={id}
          initialBoxes={cr.boxesAffected}
          reportedAt={cr.boxesAffectedReportedAt}
          onClose={() => setCloseOpen(false)}
          onDone={(updated) => {
            // Belt-and-suspenders: cache the new value AND refetch the canonical
            // record in case the response shape lacks an embedded relation.
            qc.setQueryData(['credit-request', id], updated);
            qc.invalidateQueries({ queryKey: ['credit-request', id] });
            qc.invalidateQueries({ queryKey: ['credit-requests'] });
            setCloseOpen(false);
          }}
        />
      )}

      {confirmDelete && (
        <DeleteRequestDialog
          claimNumber={cr.claimNumber}
          pending={deleteMut.isPending}
          error={actionError}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => deleteMut.mutate()}
        />
      )}

      {reportBoxesOpen && (
        <ReportBoxesDialog
          requestId={id}
          claimNumber={cr.claimNumber}
          initial={cr.boxesAffected}
          onClose={() => setReportBoxesOpen(false)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['credit-request', id] });
            qc.invalidateQueries({ queryKey: ['credit-requests'] });
            qc.invalidateQueries({ queryKey: ['messages', id] });
            setReportBoxesOpen(false);
          }}
        />
      )}

      {confirmTakeover && cr.agent && (
        <TakeoverDialog
          currentAgentName={cr.agent.name}
          claimNumber={cr.claimNumber}
          pending={assignMut.isPending}
          error={actionError}
          onCancel={() => setConfirmTakeover(false)}
          onConfirm={() => {
            assignMut.mutate(undefined, {
              onSuccess: () => setConfirmTakeover(false),
            });
          }}
        />
      )}
    </div>
  );
}

function ReportBoxesDialog({
  requestId,
  claimNumber,
  initial,
  onClose,
  onDone,
}: {
  requestId: string;
  claimNumber: string;
  initial: number | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [boxes, setBoxes] = useState(initial != null ? String(initial) : '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const mut = useMutation({
    mutationFn: (n: number) =>
      api(`/api/v1/credit-requests/${requestId}/report-boxes`, {
        method: 'POST',
        body: { boxesAffected: n },
      }),
    onSuccess: () => onDone(),
    onError: (err) => setError(err instanceof Error ? err.message : 'No se pudo enviar'),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const n = Number(boxes);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      setError('Ingresa un número válido (0 o más).');
      return;
    }
    mut.mutate(n);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => !mut.isPending && onClose()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-xl"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">Reportar cajas afectadas</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            aria-label="Cerrar"
            className="text-slate-400 hover:text-slate-700 text-3xl leading-none"
          >
            ×
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <p className="text-base text-slate-600">
            ¿Cuántas cajas finalmente quedaron malas en <span className="font-bold">{claimNumber}</span>?
          </p>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={boxes}
            onChange={(e) => setBoxes(e.target.value)}
            autoFocus
            placeholder="Ej. 5"
            className="w-full px-4 py-4 text-2xl font-bold text-center border-2 border-slate-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
          />
          {error && (
            <p className="text-base text-rose-700 bg-rose-50 border-2 border-rose-200 rounded-xl p-3 font-medium">
              {error}
            </p>
          )}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={mut.isPending}
              className="flex-1 px-4 py-3.5 border-2 border-slate-300 rounded-full text-slate-700 text-base font-semibold disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending || boxes === ''}
              className="flex-1 px-4 py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-base font-bold rounded-full shadow-md"
            >
              {mut.isPending ? '…' : 'Enviar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TakeoverDialog({
  currentAgentName,
  claimNumber,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  currentAgentName: string;
  claimNumber: string;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onCancel();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onCancel, pending]);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => !pending && onCancel()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-xl"
      >
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-2xl font-bold text-slate-900">Tomar caso</h2>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-base text-slate-700 leading-relaxed">
            El crédito <span className="font-bold">{claimNumber}</span> lo está
            atendiendo <span className="font-bold">{currentAgentName}</span>. Si lo
            tomas, pasará a tu cuenta y {currentAgentName.split(' ')[0]} dejará de
            ser el responsable.
          </p>
          {error && (
            <p className="text-base text-rose-700 bg-rose-50 border-2 border-rose-200 rounded-xl p-3 font-medium">
              {error}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={pending}
              className="flex-1 px-4 py-3.5 border-2 border-slate-300 rounded-full text-slate-700 text-base font-semibold disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={pending}
              className="flex-1 px-4 py-3.5 bg-brand-600 hover:bg-brand-700 text-white text-base font-bold rounded-full shadow-md disabled:opacity-50"
            >
              {pending ? '…' : 'Tomar caso'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteRequestDialog({
  claimNumber,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  claimNumber: string;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onCancel();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onCancel, pending]);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => !pending && onCancel()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-xl"
      >
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-2xl font-bold text-slate-900">Eliminar crédito</h2>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-base text-slate-700 leading-relaxed">
            ¿Eliminar permanentemente el crédito <span className="font-bold">{claimNumber}</span>?
            Se borrarán todos los mensajes, fotos y actividad asociada. Esta acción
            no se puede deshacer.
          </p>
          {error && (
            <p className="text-base text-rose-700 bg-rose-50 border-2 border-rose-200 rounded-xl p-3 font-medium">
              {error}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={pending}
              className="flex-1 px-4 py-3.5 border-2 border-slate-300 rounded-full text-slate-700 text-base font-semibold disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={pending}
              className="flex-1 px-4 py-3.5 bg-rose-600 hover:bg-rose-700 text-white text-base font-bold rounded-full shadow-md disabled:opacity-50"
            >
              {pending ? '…' : 'Eliminar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CloseDialog({
  requestId,
  initialBoxes,
  reportedAt,
  onClose,
  onDone,
}: {
  requestId: string;
  initialBoxes: number | null;
  reportedAt: string | null;
  onClose: () => void;
  onDone: (updated: CreditRequest) => void;
}) {
  const [boxes, setBoxes] = useState(initialBoxes != null ? String(initialBoxes) : '');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [amount, setAmount] = useState('');
  const [confirmDoc, setConfirmDoc] = useState(false);
  const [phase, setPhase] = useState<'form' | 'closing' | 'uploading' | 'partial'>('form');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase === 'form') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, phase]);

  const hasFile = !!file;
  const canSubmit =
    !!boxes &&
    phase === 'form' &&
    (!hasFile || (!!amount && confirmDoc));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);

    setPhase('closing');
    let updated: CreditRequest;
    try {
      updated = await api<CreditRequest>(`/api/v1/credit-requests/${requestId}/close`, {
        method: 'POST',
        body: {
          boxesAffected: Number(boxes),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      });
    } catch (err) {
      setPhase('form');
      setError(err instanceof Error ? err.message : 'No se pudo cerrar el crédito');
      return;
    }

    if (!hasFile) {
      onDone(updated);
      return;
    }

    setPhase('uploading');
    try {
      await uploadDocument(
        `/api/v1/credit-requests/${requestId}/document`,
        file!,
        Number(amount),
        getAccessToken(),
      );
      onDone(updated);
    } catch (err) {
      setPhase('partial');
      setError(
        (err instanceof Error ? err.message : 'Error subiendo documento') +
          ' — el crédito se cerró correctamente, puedes subir el documento desde la sección de abajo.',
      );
      // Reflect the closed state immediately so the underlying page updates.
      onDone(updated);
    }
  };

  const busy = phase === 'closing' || phase === 'uploading';

  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => phase === 'form' && onClose()}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-xl max-h-[92dvh] overflow-y-auto"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-slate-900">Cerrar crédito</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Cerrar"
            className="text-slate-400 hover:text-slate-700 disabled:text-slate-200 text-3xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-5">
          {reportedAt && initialBoxes != null && (
            <p className="text-base text-sky-900 bg-sky-50 border-2 border-sky-200 rounded-xl p-3">
              💬 El encargado reportó <span className="font-bold">{initialBoxes}</span> caja
              {initialBoxes === 1 ? '' : 's'} el {format(new Date(reportedAt), 'dd/MM HH:mm')}.
              Confírmalo o ajústalo abajo.
            </p>
          )}
          <label className="block">
            <span className="text-base font-semibold text-slate-700 mb-1.5 block">
              Cajas afectadas <span className="text-rose-600">*</span>
            </span>
            <input
              type="number"
              min={0}
              inputMode="numeric"
              required
              value={boxes}
              onChange={(e) => setBoxes(e.target.value)}
              placeholder="Ej. 3"
              disabled={busy}
              className="w-full px-4 py-3.5 text-lg border-2 border-slate-300 rounded-xl focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none disabled:bg-slate-50"
            />
          </label>

          <label className="block">
            <span className="text-base font-semibold text-slate-700 mb-1.5 block">Notas (opcional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Detalles adicionales para el encargado…"
              disabled={busy}
              className="w-full px-4 py-3 text-base border-2 border-slate-300 rounded-xl focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none resize-none disabled:bg-slate-50"
            />
          </label>

          <div className="border-t border-slate-200 pt-5">
            <p className="text-base font-semibold text-slate-800 mb-1">
              📄 Adjuntar documento de crédito (opcional)
            </p>
            <p className="text-sm text-slate-500 mb-3">
              Si lo añades aquí, se enviará al encargado al cerrar. Si no, puedes hacerlo después desde la sección de abajo.
            </p>

            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={busy}
              className="block w-full text-base mb-3"
            />

            {hasFile && (
              <>
                <label className="block mb-3">
                  <span className="text-base font-semibold text-slate-700 mb-1.5 block">
                    Monto del crédito $ <span className="text-rose-600">*</span>
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    inputMode="decimal"
                    required={hasFile}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Ej. 245.00"
                    disabled={busy}
                    className="w-full px-4 py-3.5 text-lg border-2 border-slate-300 rounded-xl focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none disabled:bg-slate-50"
                  />
                </label>

                <label className="flex items-center gap-3 text-base text-slate-800 font-medium">
                  <input
                    type="checkbox"
                    checked={confirmDoc}
                    onChange={(e) => setConfirmDoc(e.target.checked)}
                    disabled={busy}
                    className="w-5 h-5"
                  />
                  Confirmo monto y archivo
                </label>
              </>
            )}
          </div>

          {phase === 'closing' && (
            <p className="text-base text-sky-700 bg-sky-50 border-2 border-sky-200 rounded-xl p-3 font-medium">
              ⏳ Cerrando crédito…
            </p>
          )}
          {phase === 'uploading' && (
            <p className="text-base text-sky-700 bg-sky-50 border-2 border-sky-200 rounded-xl p-3 font-medium">
              📤 Subiendo documento al encargado…
            </p>
          )}
          {error && (
            <p className="text-base text-rose-700 bg-rose-50 border-2 border-rose-200 rounded-xl p-3 font-medium">
              {error}
            </p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex gap-3 sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-4 py-3.5 border-2 border-slate-300 rounded-full text-slate-700 text-base font-semibold disabled:opacity-50"
          >
            {phase === 'partial' ? 'Cerrar' : 'Cancelar'}
          </button>
          {phase !== 'partial' && (
            <button
              type="submit"
              disabled={!canSubmit || busy}
              className="flex-1 px-4 py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-base font-bold rounded-full shadow-md"
            >
              {busy ? '…' : hasFile ? 'Cerrar y enviar documento' : 'Cerrar crédito'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function PhotoGallery({
  requestId,
  photos,
  isStaff,
}: {
  requestId: string;
  photos: CreditPhoto[];
  isStaff: boolean;
}) {
  const qc = useQueryClient();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const rotateMut = useMutation({
    mutationFn: async (next: CreditPhoto[]) => {
      const updated = await api<CreditRequest>(`/api/v1/credit-requests/${requestId}/photos`, {
        method: 'PATCH',
        body: { photos: next },
      });
      return updated;
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ['credit-request', requestId] });
      const prev = qc.getQueryData<CreditRequest>(['credit-request', requestId]);
      if (prev) qc.setQueryData(['credit-request', requestId], { ...prev, photos: next });
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev) qc.setQueryData(['credit-request', requestId], ctx.prev);
    },
    onSuccess: (data) => qc.setQueryData(['credit-request', requestId], data),
  });

  const rotatePhoto = (url: string, delta: 90 | -90) => {
    if (!isStaff) return;
    const next = photos.map((p) => {
      if (p.url !== url) return p;
      const cur = p.rotation ?? 0;
      const r = ((cur + delta + 360) % 360) as 0 | 90 | 180 | 270;
      return { ...p, rotation: r };
    });
    rotateMut.mutate(next);
  };

  const groups = {
    invoice: photos.filter((p) => p.type === 'invoice'),
    product: photos.filter((p) => p.type === 'product'),
    label: photos.filter((p) => p.type === 'label'),
    other: photos.filter((p) => !p.type || p.type === 'other'),
  };
  const sections: { key: keyof typeof groups; label: string; emoji: string }[] = [
    { key: 'invoice', label: 'Factura', emoji: '📄' },
    { key: 'product', label: 'Productos', emoji: '📦' },
    { key: 'label', label: 'Etiquetas', emoji: '🏷️' },
    { key: 'other', label: 'Otras', emoji: '🖼️' },
  ];

  const activePhoto = lightboxUrl ? photos.find((p) => p.url === lightboxUrl) : null;

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 space-y-4">
        {sections.map(({ key, label, emoji }) =>
          groups[key].length === 0 ? null : (
            <div key={key}>
              <div className="text-base font-semibold text-slate-700 mb-2">
                {emoji} {label} ({groups[key].length})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {groups[key].map((p, i) => (
                  <button
                    key={`${key}-${i}`}
                    type="button"
                    onClick={() => setLightboxUrl(p.url)}
                    className="aspect-square bg-slate-100 rounded-xl overflow-hidden block ring-1 ring-slate-200 active:opacity-70 transition"
                  >
                    <img
                      src={p.url}
                      alt={`${label} ${i + 1}`}
                      className="w-full h-full object-cover transition-transform"
                      style={p.rotation ? { transform: `rotate(${p.rotation}deg)` } : undefined}
                    />
                  </button>
                ))}
              </div>
            </div>
          ),
        )}
      </div>
      {activePhoto && (
        <Lightbox
          photo={activePhoto}
          isStaff={isStaff}
          onRotate={(delta) => rotatePhoto(activePhoto.url, delta)}
          onClose={() => setLightboxUrl(null)}
        />
      )}
    </>
  );
}

function Lightbox({
  photo,
  isStaff,
  onRotate,
  onClose,
}: {
  photo: CreditPhoto;
  isStaff: boolean;
  onRotate: (delta: 90 | -90) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const rot = photo.rotation ?? 0;

  return (
    <div
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-2"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Cerrar"
        className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 active:bg-white/40 text-white text-2xl font-bold flex items-center justify-center backdrop-blur"
      >
        ✕
      </button>
      <img
        src={photo.url}
        alt=""
        className="max-w-full max-h-full object-contain transition-transform"
        style={{ transform: `rotate(${rot}deg)` }}
        onClick={(e) => e.stopPropagation()}
      />
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        {isStaff && (
          <>
            <button
              type="button"
              onClick={() => onRotate(-90)}
              aria-label="Rotar a la izquierda"
              className="w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white text-xl font-semibold backdrop-blur"
            >
              ↺
            </button>
            <button
              type="button"
              onClick={() => onRotate(90)}
              aria-label="Rotar a la derecha"
              className="w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white text-xl font-semibold backdrop-blur"
            >
              ↻
            </button>
          </>
        )}
        <a
          href={photo.url}
          download
          className="px-5 py-3 bg-white/15 hover:bg-white/30 text-white text-base font-semibold rounded-full backdrop-blur"
        >
          ⬇ Descargar
        </a>
      </div>
    </div>
  );
}

function DocumentUploadSection({ requestId }: { requestId: string }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [amount, setAmount] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || !amount || !confirm) return;
    setBusy(true);
    setError(null);
    try {
      await uploadDocument(
        `/api/v1/credit-requests/${requestId}/document`,
        file,
        Number(amount),
        getAccessToken(),
      );
      qc.invalidateQueries({ queryKey: ['credit-request', requestId] });
      setFile(null);
      setAmount('');
      setConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir el documento');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="bg-amber-50 rounded-2xl border-2 border-amber-200 p-5 space-y-4">
      <h3 className="text-lg font-bold text-amber-900">Subir documento de crédito</h3>
      <input
        type="file"
        accept="application/pdf"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        className="block w-full text-base"
        required
      />
      <input
        type="number"
        step="0.01"
        min="0"
        inputMode="decimal"
        placeholder="Monto $"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="w-full px-4 py-3.5 text-lg border-2 border-amber-300 rounded-xl"
        required
      />
      <label className="flex items-center gap-3 text-base text-amber-900 font-medium">
        <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="w-5 h-5" />
        Confirmo monto y archivo
      </label>
      {error && <p className="text-base text-rose-700 font-medium">{error}</p>}
      <button
        type="submit"
        disabled={busy || !file || !amount || !confirm}
        className="w-full px-5 py-3.5 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white text-lg font-bold rounded-full shadow-md"
      >
        {busy ? 'Enviando…' : 'Enviar documento al cliente'}
      </button>
    </form>
  );
}

function DocumentReadySection({
  requestId,
  url,
  amount,
  claimNumber,
  isStaff,
}: {
  requestId: string;
  url: string;
  amount: string | number | null;
  claimNumber: string;
  isStaff: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const onResend = async () => {
    setBusy(true);
    try {
      await api(`/api/v1/credit-requests/${requestId}/document/resend`, { method: 'POST' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="bg-emerald-50 rounded-2xl border-2 border-emerald-200 p-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-lg font-bold text-emerald-900">📄 Crédito disponible</p>
          {amount != null && <p className="text-base text-emerald-800 mt-1">Monto: ${Number(amount).toFixed(2)}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setViewerOpen(true)}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-base font-semibold rounded-full"
          >
            Ver documento
          </button>
          {isStaff && (
            <button
              onClick={onResend}
              disabled={busy}
              className="px-5 py-2.5 border-2 border-emerald-300 text-emerald-800 text-base font-semibold rounded-full"
            >
              {busy ? '…' : 'Reenviar'}
            </button>
          )}
        </div>
      </div>
      {viewerOpen && (
        <PdfViewer
          url={url}
          filename={`${claimNumber}.pdf`}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </>
  );
}

function PdfViewer({
  url,
  filename,
  onClose,
}: {
  url: string;
  filename: string;
  onClose: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [iframeError, setIframeError] = useState(false);

  // iOS Safari (and especially standalone PWAs) doesn't render PDFs inside
  // <iframe> — it shows a blank page. Detect and offer "open in browser"
  // instead, which forces native PDF rendering.
  const isIOS =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as { MSStream?: unknown }).MSStream;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Programmatic download via blob — works on iOS standalone PWA where
  // <a download> is silently ignored.
  const onDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Error al descargar');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/95 z-50 flex flex-col"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white shrink-0">
        <span className="font-semibold text-base truncate">📄 {filename}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-2xl"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 bg-slate-800 overflow-hidden">
        {isIOS ? (
          <div className="h-full flex items-center justify-center text-white text-center p-6">
            <div className="max-w-sm">
              <div className="text-6xl mb-4">📄</div>
              <p className="text-xl font-semibold mb-2">{filename}</p>
              <p className="text-base text-white/70 mb-6">
                iOS no permite previsualizar PDFs dentro de la app. Ábrelo en el
                navegador o descárgalo a tu teléfono.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full py-4 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white text-lg font-bold rounded-2xl shadow-md"
              >
                🔍 Abrir PDF
              </a>
            </div>
          </div>
        ) : !iframeError ? (
          <iframe
            src={url}
            title={filename}
            className="w-full h-full border-0 bg-white"
            onError={() => setIframeError(true)}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-white text-center p-6">
            <div>
              <div className="text-5xl mb-3">📄</div>
              <p className="text-lg mb-4">No se puede mostrar el PDF en pantalla.</p>
              <p className="text-base text-white/70">Toca "Descargar" para guardarlo en tu dispositivo.</p>
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-3 bg-slate-900 flex flex-col gap-2 shrink-0">
        {downloadError && (
          <p className="text-sm text-rose-300 text-center">{downloadError}</p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white text-base font-semibold rounded-full"
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-500 text-white text-base font-bold rounded-full"
          >
            {downloading ? 'Descargando…' : '⬇ Descargar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Chat({ requestId, messages, isLoading }: { requestId: string; messages: RequestMessage[]; isLoading: boolean }) {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const ordered = useMemo(
    () => [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [messages],
  );

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [ordered.length]);

  useEffect(() => {
    if (!me) return;
    const s = getSocket();
    const unread = ordered
      .filter((m) => !m.readAt && m.fromUserId !== me.id)
      .map((m) => m.id);
    if (unread.length > 0) {
      s.emit('message:read', { requestId, messageIds: unread });
    }
  }, [ordered, me, requestId]);

  const onSend = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !me) return;
    setSending(true);
    const clientId = ulid();
    const optimistic: RequestMessage = {
      id: clientId,
      clientId,
      requestId,
      claimNumber: '',
      fromUserId: me.id,
      fromAgentId: me.agent?.id ?? null,
      phoneNumber: me.phone ?? '',
      direction: me.roles.some((r) => r === 'admin' || r === 'customer_service') ? 'outbound' : 'inbound',
      messageText: trimmed,
      status: 'sent',
      attachments: null,
      createdAt: new Date().toISOString(),
      readAt: null,
    };
    qc.setQueryData<MessagesPage>(['messages', requestId], (prev) =>
      prev ? { ...prev, items: [...prev.items, optimistic] } : { items: [optimistic], nextCursor: null },
    );
    setText('');
    try {
      const real = await api<RequestMessage>(`/api/v1/credit-requests/${requestId}/messages`, {
        method: 'POST',
        body: { clientId, text: trimmed },
      });
      qc.setQueryData<MessagesPage>(['messages', requestId], (prev) => {
        if (!prev) return { items: [real], nextCursor: null };
        return {
          ...prev,
          items: prev.items.map((m) => (m.clientId === clientId ? real : m)),
        };
      });
    } catch (err) {
      qc.setQueryData<MessagesPage>(['messages', requestId], (prev) => {
        if (!prev) return prev;
        return { ...prev, items: prev.items.filter((m) => m.clientId !== clientId) };
      });
      alert(err instanceof Error ? err.message : 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col h-[60dvh] sm:h-[55dvh] overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
        {isLoading && <div className="text-center text-base text-slate-400 py-8">Cargando mensajes…</div>}
        {!isLoading && ordered.length === 0 && (
          <div className="text-center text-base text-slate-400 py-12 px-4">
            No hay mensajes aún. Escribe el primero abajo.
          </div>
        )}
        {ordered.map((m) => (
          <Bubble key={m.id} m={m} mine={m.fromUserId === me?.id} />
        ))}
      </div>
      <form onSubmit={onSend} className="border-t border-slate-200 p-3 flex gap-2 bg-white">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escribe un mensaje…"
          className="flex-1 px-4 py-3 rounded-full border-2 border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none text-base"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          className="px-5 py-3 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-base font-bold rounded-full"
        >
          Enviar
        </button>
      </form>
    </div>
  );
}

function Bubble({ m, mine }: { m: RequestMessage; mine: boolean }) {
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 shadow-sm text-base ${
          mine ? 'bg-brand-600 text-white rounded-br-sm' : 'bg-white text-slate-900 rounded-bl-sm border border-slate-200'
        }`}
      >
        <div className="whitespace-pre-wrap break-words leading-snug">{m.messageText}</div>
        <div className={`mt-1 text-xs flex justify-end items-center gap-1 ${mine ? 'text-brand-100' : 'text-slate-400'}`}>
          <span>{format(new Date(m.createdAt), 'HH:mm')}</span>
          {mine && <Tick status={m.status} read={!!m.readAt} />}
        </div>
      </div>
    </div>
  );
}

function Tick({ status, read }: { status: RequestMessage['status']; read: boolean }) {
  if (read) return <span title="Leído">✓✓</span>;
  if (status === 'delivered') return <span title="Entregado">✓✓</span>;
  return <span title="Enviado">✓</span>;
}

function StatusBadge({ status }: { status: CreditRequest['status'] }) {
  const cls =
    status === 'open' ? 'bg-amber-100 text-amber-800' :
    status === 'processing' ? 'bg-sky-100 text-sky-800' :
    'bg-emerald-100 text-emerald-800';
  const label = status === 'open' ? 'Abierta' : status === 'processing' ? 'En proceso' : 'Cerrada';
  return <span className={`text-sm uppercase font-bold px-3 py-1 rounded-full ${cls}`}>{label}</span>;
}
